import { SettingsFromApi } from '@/backend/settings/get-settings/types';
import { useUser } from '@privy-io/react-auth';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

/* -------------------------------------------------------------------------- */

enum LocalStorageKeysEnum {
	// SETTINGS = 'settings',
	LOCAL_STORAGE_SETTINGS = 'local_storage_settings',
}

interface LocalStorageSettings {
	sideNavOpen: boolean;
}

export enum UserVisibilityEnum {
	GEO_BLOCKED = 'geo_blocked',
	MAINTENANCE = 'maintenance',
	OK = 'ok',
	PENDING = 'pending',
}

// interface Settings {
// 	shouldAnimateBanner: boolean;
// 	showFiat: boolean;
// 	settingsFromApi: SettingsFromApi ;
// 	localStorageSettings: LocalStorageSettings ;
// }
interface SettingsStore {
	userVisibility: UserVisibilityEnum;
	shouldAnimateBanner: boolean;
	showFiat: boolean;
	zeroBalancesHidden: boolean;
	settingsFromApi: SettingsFromApi | null;
	localStorageSettings: LocalStorageSettings;
	previousRoute: string | null;
	actions: {
		setUserVisibility: (userVisibility: UserVisibilityEnum) => void;
		setSideNavOpen: (open: boolean) => void;
		setShowFiat: (show: boolean) => void;
		setZeroBalancesHidden: (show: boolean) => void;
		setShouldAnimateBanner: (shouldAnimateBanner: boolean) => void;
		setPreviousRoute: (previousRoute: string | null) => void;
	};
}

const getLocalStorageSettings = (): LocalStorageSettings => {
	if (typeof window === 'undefined') return { sideNavOpen: true };
	const stored = localStorage.getItem(
		LocalStorageKeysEnum.LOCAL_STORAGE_SETTINGS
	);

	return stored ? JSON.parse(stored) : { sideNavOpen: true };
};

const initialState: Omit<SettingsStore, 'actions'> = {
	localStorageSettings: getLocalStorageSettings(),
	userVisibility: UserVisibilityEnum.PENDING,
	shouldAnimateBanner: false,
	showFiat: false,
	zeroBalancesHidden: false,
	settingsFromApi: null,
	previousRoute: null,
};

const settingsStore = create<SettingsStore>((set) => {
	const store: SettingsStore = {
		...initialState,

		actions: {
			setUserVisibility: (userVisibility) => {
				set((state) => ({
					userVisibility,
				}));
			},
			setSideNavOpen: (open) => {
				set((state) => ({
					localStorageSettings: {
						...state.localStorageSettings,
						sideNavOpen: open,
					},
				}));
			},
			setShowFiat: (show) => {
				set((state) => ({
					showFiat: show,
				}));
			},
			setZeroBalancesHidden: (show) => {
				set((state) => ({
					zeroBalancesHidden: show,
				}));
			},
			setShouldAnimateBanner: (shouldAnimateBanner) => {
				set((state) => ({
					shouldAnimateBanner,
				}));
			},
			setPreviousRoute: (previousRoute) => {
				set((state) => ({
					previousRoute,
				}));
			},
		},
	};

	return store;
});

/* -------------------------------------------------------------------------- */

if (typeof window !== 'undefined') {
	settingsStore.subscribe((state: SettingsStore) => {
		const settings = state.localStorageSettings;
		localStorage.setItem(
			LocalStorageKeysEnum.LOCAL_STORAGE_SETTINGS,
			JSON.stringify(settings)
		);
	});
}

/* -------------------------------------------------------------------------- */
export const useSetShouldAnimateBanner = () =>
	settingsStore((state) => state.actions.setShouldAnimateBanner);
export const useUserVisibility = () =>
	settingsStore(useShallow((state) => state.userVisibility));

export const useSideNavOpen = () =>
	settingsStore(useShallow((state) => state.localStorageSettings.sideNavOpen));

export const useShowFiat = () =>
	settingsStore(useShallow((state) => state.showFiat));
export const useZeroBalancesHidden = () =>
	settingsStore(useShallow((state) => state.zeroBalancesHidden));
export const usePreviousRoute = () =>
	settingsStore(useShallow((state) => state.previousRoute));

export const useSettingsStoreActions = () =>
	settingsStore((state) => state.actions);
